import { useCallback, useMemo, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import UploadIcon from "@mui/icons-material/Upload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { TrainingType } from "../../types/training";
import {
  useInstructorTraining,
  createAttendanceBook,
} from "../../api/instructorApi";
import {
  useDeleteGallery,
  useRetrieveTraining,
  useUploadAttendanceBook,
  useUploadGallery,
} from "../../api/trainingApi";
import { InstructorTrainingTableDef } from "../../table/TrainingTableDef";
import { FileUploadDialog } from "../../components/FileUpload";
import "./instructor.scss";

const AttendanceBook = ({ training }: { training: TrainingType }) => {
  const { id, instructor_detail, attendance_book_url } = training;
  const { mutateAsync: uploadAttendanceBook } = useUploadAttendanceBook({
    id: training.id,
  });
  const [isUpload, setIsUpload] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const filename = useMemo(
    () => `출석부_${instructor_detail.corp_name}_${id}.xlsx`,
    [id, instructor_detail]
  );

  const handleCreate = useCallback(async () => {
    setIsCreating(true);
    const result = await createAttendanceBook(id);
    const url = window.URL.createObjectURL(new Blob([result.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
    setIsCreating(false);
  }, [filename, id]);

  return (
    <div>
      <h4 className="subtitle">출석부</h4>
      <div className="instructor-wrapper">
        <button
          onClick={handleCreate}
          disabled={isCreating}
          className="instructor-button"
        >
          생성하기
        </button>
        <button className="instructor-button" onClick={() => setIsUpload(true)}>
          <UploadIcon /> 업로드하기
        </button>
        {attendance_book_url && (
          <button>
            <a className="instructor-button" href={attendance_book_url}>
              업로드한 파일 다운로드
            </a>
          </button>
        )}
      </div>
      {isUpload && (
        <FileUploadDialog
          title="출석부"
          upload={uploadAttendanceBook}
          close={() => setIsUpload(false)}
        />
      )}
    </div>
  );
};

const Gallery = ({ training }: { training: TrainingType }) => {
  const { id, gallery_urls } = training;
  const [isUpload, setIsUpload] = useState(false);
  const { mutateAsync: uploadGallery } = useUploadGallery({ id });
  const { mutateAsync: deleteGallery } = useDeleteGallery({ id });

  return (
    <div>
      <h4 className="subtitle">실습 사진</h4>
      <div className="instructor-wrapper">
        <button className="instructor-button" onClick={() => setIsUpload(true)}>
          사진 추가
        </button>
      </div>
      <div>
        {gallery_urls.map(({ filename, url }) => {
          return (
            <div className="horizontal">
              <p>{filename}</p>
              <a
                className="no-border-button"
                href={url}
                target="_blank"
                rel="noreferrer"
              >
                <VisibilityIcon />
              </a>
              <button
                className="no-border-button warning"
                onClick={async () => {
                  if (window.confirm("정말 삭제하시겠습니까?")) {
                    await deleteGallery(filename);
                    alert("삭제되었습니다.");
                  }
                }}
              >
                <DeleteOutlineIcon color="warning" />
              </button>
            </div>
          );
        })}
      </div>
      {isUpload && (
        <FileUploadDialog
          title="실습 사진"
          upload={uploadGallery}
          close={() => setIsUpload(false)}
          isMulti
        />
      )}
    </div>
  );
};

const InstructorHome = () => {
  const { data: trainingList } = useInstructorTraining();
  const [detailTrainingId, setDetailTrainingId] = useState<number>();
  const { data: detailTraining } = useRetrieveTraining({
    id: detailTrainingId,
  });

  const trainingTable = useMaterialReactTable<TrainingType>({
    data: trainingList?.trainings || [],
    columns: InstructorTrainingTableDef(),
    defaultColumn: { minSize: 0, maxSize: 200, size: 0 },
    enableFilters: false,
    enablePagination: false,
    enableSorting: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableColumnActions: false,
    enableFullScreenToggle: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    state: {
      density: "compact",
    },
    muiTableBodyCellProps: { align: "center" },
    muiTableHeadCellProps: { align: "center" },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        setDetailTrainingId(row.original.id);
      },
    }),
    muiTableFooterProps: {},
  });

  return (
    <div style={{ marginTop: "-30px" }}>
      <h3>실습 목록</h3>
      <MaterialReactTable table={trainingTable} />
      {detailTraining ? (
        <div>
          <h3>선택한 실습 id: {detailTrainingId}</h3>
          <AttendanceBook training={detailTraining} />
          <Gallery training={detailTraining} />
        </div>
      ) : (
        <h4>실습을 선택하세요.</h4>
      )}
    </div>
  );
};

export default InstructorHome;
